import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { STUDIO, HERO_SLIDES } from "../data/siteContent";
import { scrollToId } from "../hooks/useLenis";
import { EASE } from "./Reveal";

const SLIDE_MS = 6000;

export default function Hero() {
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll();
  const contentY = useTransform(scrollYProgress, [0, 0.4], [0, 180]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.35], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), SLIDE_MS);
    return () => clearInterval(t);
  }, []);

  const lines = ["Every Moment.", "Forever Framed."];

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[560px] overflow-hidden">
      {/* Slideshow with slow Ken Burns drift */}
      <AnimatePresence>
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: "easeInOut" }}
        >
          <motion.img
            src={HERO_SLIDES[index].src}
            alt={HERO_SLIDES[index].alt}
            fetchpriority={index === 0 ? "high" : undefined}
            loading={index === 0 ? "eager" : "lazy"}
            className="h-full w-full object-cover"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1.18 }}
            transition={{ duration: SLIDE_MS / 1000 + 1.6, ease: "linear" }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Cinematic overlays */}
      <div className="absolute inset-0 bg-ink/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/40" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent" />

      {/* Content with parallax exit */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center text-center"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.9, ease: EASE }}
          className="eyebrow flex items-center gap-3 mb-6"
        >
          <span className="h-px w-8 bg-gold/60 hidden sm:block" />
          {STUDIO.eyebrow}
          <span className="h-px w-8 bg-gold/60 hidden sm:block" />
        </motion.p>

        <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-semibold leading-[1.05] tracking-tight text-ivory">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-2">
              <motion.span
                className={`block ${i === 1 ? "italic text-gold" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.75 + i * 0.18, duration: 1.1, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: EASE }}
          className="mt-6 text-ivory-dim text-base sm:text-lg font-light tracking-wide"
        >
          {STUDIO.taglineHi} <span className="text-gold/80">—</span> {STUDIO.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <button
            data-testid="hero-book-date-btn"
            onClick={() => scrollToId("book")}
            className="w-64 sm:w-auto bg-gold text-ink text-[13px] font-semibold uppercase tracking-[0.2em] px-10 py-4 hover:bg-gold-hover hover:-translate-y-0.5 transition-all duration-300"
          >
            Book Your Date
          </button>
          <button
            data-testid="hero-view-portfolio-btn"
            onClick={() => scrollToId("portfolio")}
            className="w-64 sm:w-auto border border-ivory/30 text-ivory text-[13px] uppercase tracking-[0.2em] px-10 py-4 hover:border-gold hover:text-gold transition-all duration-300"
          >
            View Portfolio
          </button>
        </motion.div>
      </motion.div>

      {/* Slide progress + scroll cue */}
      <div className="absolute bottom-8 inset-x-0 z-10 px-6 flex items-end justify-between">
        <div className="hidden sm:flex flex-col gap-3 w-40" data-testid="hero-slide-indicators">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.src}
              data-testid={`hero-slide-indicator-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className="group py-1.5 flex items-center"
            >
              <span className="relative h-[2px] w-full bg-ivory/20 overflow-hidden">
                {i === index ? (
                  <motion.span
                    key={index}
                    className="absolute inset-y-0 left-0 bg-gold"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: SLIDE_MS / 1000, ease: "linear" }}
                  />
                ) : (
                  <span
                    className={`absolute inset-y-0 left-0 bg-ivory/50 ${i < index ? "w-full" : "w-0"}`}
                  />
                )}
              </span>
            </button>
          ))}
        </div>
        <button
          data-testid="hero-scroll-cue"
          onClick={() => scrollToId("about")}
          aria-label="Scroll down"
          className="mx-auto sm:mx-0 flex flex-col items-center gap-1 text-ivory-dim hover:text-gold transition-colors"
        >
          <span className="text-[10px] uppercase tracking-[0.35em]">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ChevronDown className="w-5 h-5" />
          </motion.span>
        </button>
        <div className="hidden sm:block w-40 text-right text-[10px] uppercase tracking-[0.3em] text-ivory-mute">
          {String(index + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
        </div>
      </div>
    </section>
  );
}
